import {
    IGPSData,
    IPolygon,
    IAnalysisItem,
    IGPSBenchmarkCommonData,
    IBrand,
    IGCPBigQueryJob,
    IFrequentlyVisitedPlace,
} from "../Models/Models";
import { GPSInsightsContainerView } from "../Enums/Enums";
import {
    SET_LOADING_GPS_MOBILE_INSIGHTS_REPORT,
    START_GPS_MOBILE_INSIGHTS_REPORT,
    SET_GPS_INSIGHT_MODAL_VISIBLE,
    SET_LOADING_BRAND,
    SET_LOADING_JOBS,
    GET_BRAND_SUCCESS,
    SET_GPS_INSIGHT_RESULTS,
    RESET_GPS_INSIGHT_RESULTS,
    SET_INSIGHTS_COLLABORATING,
    SAVE_TO_SCOUT_SUCCESS,
    SET_LOADING_SAVE_TO_SCOUT,
    SET_CURRENT_GPS_INSIGHT_VIEW,
    CLEAR_FINISHED_INSIGHTS_LISTS,
    CLEAR_RUNNING_AND_FINISHED_INSIGHTS_LISTS,
    REMOVE_FINISHED_INSIGHT_FROM_RUNNING_INSIGHTS,
    ADD_FINISHED_INSIGHT_TO_LIST,
    ADD_RUNNING_INSIGHT_TO_LIST,
    SET_POLYGON_CENTROID,
    SET_MOBILYTICS_SCENARIOS,
    SET_FVP_MARKERS,
    SET_SELECTED_FVP,
    UPDATE_GPS_INSIGHT_JOB,
    PROCESS_JOBS_SUCCESS,
} from "../Actions/Types";
import { IMobilyticsScenarioLite } from "../Models/IMobilyticsScenarioLite";
import { IMapMarker } from "../Models/IMapMarker";
import { IProcessJobsResult } from "../Models/IProcessJobsResult";

declare const scout;

export interface IGPSInsightsState {
  data: IGPSData[];
  polygon: IPolygon[];
  benchmarkCommonData: IGPSBenchmarkCommonData;
  polygonCentroid: { x: number; y: number };
  loadingBrand: boolean;
  brand: IBrand | null;
  jobs: IGCPBigQueryJob[];
  mappedJobs: IAnalysisItem[];
  runningInsights: string[];
  finishedInsights: string[];
  loadingJobs: boolean;
  selectedGpsInsightForReportView: IGCPBigQueryJob[];
  selectedGpsInsightJobs: IGCPBigQueryJob[];
  currentBenchmarkJobId: string;
  currentGpsInsightView: GPSInsightsContainerView;
  isGpsInsightModalVisible: boolean;
  collaborating: boolean;
  loadingReport: boolean;
  loadingSaveToScout: boolean;
  scenarios: IMobilyticsScenarioLite[];
  fvpMapMarkers: IMapMarker[];
  selectedFVP: IFrequentlyVisitedPlace | null;
  showVisitorBehaviorsComponent: boolean;
}

export const defaultState: IGPSInsightsState = {
    data: null,
    polygon: null,
    benchmarkCommonData: null,
    polygonCentroid: { x: 0, y: 0 },
    loadingBrand: false,
    brand: null,
    jobs: [],
    mappedJobs: [],
    runningInsights: [],
    finishedInsights: [],
    loadingJobs: false,
    selectedGpsInsightForReportView: null,
    selectedGpsInsightJobs: [],
    currentBenchmarkJobId: null,
    currentGpsInsightView: GPSInsightsContainerView.All,
    isGpsInsightModalVisible: false,
    collaborating: false,
    loadingReport: false,
    loadingSaveToScout: false,
    scenarios: [],
    fvpMapMarkers: [],
    selectedFVP: null,
    showVisitorBehaviorsComponent: false,
};

export const mobilyticsReportOutput = (
    state: IGPSInsightsState = { ...defaultState },
    action
): IGPSInsightsState => {
    switch (action.type) {
    /** misc */
        case SET_FVP_MARKERS: {
            return {
                ...state,
                fvpMapMarkers: action.data as IMapMarker[],
            };
        }
        case SET_SELECTED_FVP: {
            return {
                ...state,
                selectedFVP: action.data as IFrequentlyVisitedPlace | null,
            };
        }
        case SET_LOADING_GPS_MOBILE_INSIGHTS_REPORT: {
            return {
                ...state,
                loadingReport: action.data as boolean,
            };
        }
        case START_GPS_MOBILE_INSIGHTS_REPORT: {
            scout.interop.api.mobilyticsSubmission.toggleMobilyticsFormIsVisible(
                true
            );
            return {
                ...state,
                isGpsInsightModalVisible: true,
            };
        }
        case SET_GPS_INSIGHT_MODAL_VISIBLE: {
            scout.interop.api.mobilyticsSubmission.toggleMobilyticsFormIsVisible(
                action.data as boolean
            );
            return {
                ...state,
                isGpsInsightModalVisible: action.data as boolean,
            };
        }
        case SET_LOADING_BRAND: {
            return {
                ...state,
                loadingBrand: action.data as boolean,
            };
        }
        case SET_LOADING_JOBS: {
            return {
                ...state,
                loadingJobs: action.data as boolean,
            };
        }
        case SET_INSIGHTS_COLLABORATING: {
            return {
                ...state,
                collaborating: action.data,
            };
        }
        /** success */
        case PROCESS_JOBS_SUCCESS: {
            const data = action.data as IProcessJobsResult;
            return {
                ...state,
                ...data
            };
        }

        case RESET_GPS_INSIGHT_RESULTS: {
            return {
                ...state,
                data: null,
                polygon: null,
                selectedGpsInsightForReportView: null,
            };
        }
        case SET_GPS_INSIGHT_RESULTS: {
            const initialJobIndex = 0;
            const gpsData: IGPSData[] = action.data.data as IGPSData[];
            /** GB using "some" here because I'm not sure if we'd ever have a scenario where
       * one polygon could have FVP data and the other could not. But if that were
       * to happen, this will handle it.
       */
            const fvpHasData: boolean = gpsData.some(
                (d) => d.frequentlyVisitedPlaces.length > 0
            );
            const currentBenchmarkJobId
        = action.data.job.length > initialJobIndex
            ? action.data.job[initialJobIndex].jobId
            : null;
            return {
                ...state,
                data: gpsData,
                showVisitorBehaviorsComponent: fvpHasData,
                selectedGpsInsightForReportView: action.data.job,
                benchmarkCommonData: action.data.benchmarkCommonData,
                polygon: action.data.job.map((j) => j.jobPolygon),
                selectedGpsInsightJobs: action.data.job,
                currentBenchmarkJobId,
            };
        }
        case GET_BRAND_SUCCESS: {
            /**
       * If no data was found matching the term, an empty string is sent down.
       * Otherwise, a match was found.
       */
            const brandValue: IBrand | null = action.data === "" ? null : action.data;
            return {
                ...state,
                brand: brandValue,
            };
        }
        case ADD_RUNNING_INSIGHT_TO_LIST:
            return {
                ...state,
                runningInsights: action.data as string[],
            };

        case ADD_FINISHED_INSIGHT_TO_LIST:
            return {
                ...state,
                finishedInsights: action.data as string[],
            };

        case REMOVE_FINISHED_INSIGHT_FROM_RUNNING_INSIGHTS:
            return {
                ...state,
                runningInsights: action.data as string[],
            };

        case CLEAR_RUNNING_AND_FINISHED_INSIGHTS_LISTS:
            return {
                ...state,
                runningInsights: [],
                finishedInsights: [],
            };

        case CLEAR_FINISHED_INSIGHTS_LISTS: {
            return {
                ...state,
                finishedInsights: [],
            };
        }

        case SET_CURRENT_GPS_INSIGHT_VIEW:
            return {
                ...state,
                currentGpsInsightView: action.data as GPSInsightsContainerView,
            };

        case SET_LOADING_SAVE_TO_SCOUT:
            return {
                ...state,
                loadingSaveToScout: action.data as boolean,
            };

        case SAVE_TO_SCOUT_SUCCESS: {
            if (
                state.selectedGpsInsightForReportView[0].jobId
        === (action.data as string)
            ) {
                const jobsCopy: IGCPBigQueryJob[] = [
                    ...state.selectedGpsInsightForReportView,
                ].map((j: IGCPBigQueryJob) => {
                    return {
                        ...j,
                        viewInScout: true,
                    };
                });
                return {
                    ...state,
                    selectedGpsInsightForReportView: jobsCopy,
                };
            }

            return {
                ...state,
            };
        }

        case SET_POLYGON_CENTROID:
            return {
                ...state,
                polygonCentroid: action.data as { x: number; y: number },
            };

        case SET_MOBILYTICS_SCENARIOS:
            return {
                ...state,
                scenarios: action.data,
            };

        // case DELETE_JOB_SUCCESS: {
        //   notification.success({
        //     message: "Report Deleted",
        //     duration: 2, // seconds
        //   });

        //   const deletedJobId: number = action.data;
        //   const allReportsCopy: IAnalysisItem[] = [...state.allReports].filter(
        //     (report: IAnalysisItem) => {
        //       return report.id !== deletedReportId;
        //     }
        //   );

        //   return {
        //     ...state,
        //     allReports: allReportsCopy,
        //   };
        // }
        // /** Failure */
        // case DELETE_JOB_FAILURE: {
        //   notification.error({
        //     message:
        //       "Failed to delete report. If you continue to see this message, please contact SCOUT Support.",
        //     duration: null,
        //   });
        //   return {
        //     ...state,
        //   };
        // }

        case UPDATE_GPS_INSIGHT_JOB: {
            const jobsCopy = [...state.selectedGpsInsightJobs];
            for (let i = 0; i < jobsCopy.length; i++) {
                if (jobsCopy[i].jobId === action.data.jobId) {
                    jobsCopy[i] = action.data;
                    break;
                }
            }

            return {
                ...state,
                selectedGpsInsightForReportView: [...jobsCopy],
                selectedGpsInsightJobs: [...jobsCopy],
            };
        }

        default:
            return state;
    }
};
